import { useCallback, useEffect, useState } from "react";
import {
  PUSH_SUPPORTED,
  getNotificationPermission,
  registerServiceWorker,
  requestNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
} from "../lib/pushClient";
import { t } from "../i18n";

interface UsePushSubscriptionArgs {
  enabled: boolean;
}

export const usePushSubscription = ({ enabled }: UsePushSubscriptionArgs) => {
  const [permission, setPermission] = useState<NotificationPermission>(
    PUSH_SUPPORTED ? getNotificationPermission() : "denied"
  );
  const [subscribed, setSubscribed] = useState<boolean>(false);
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!PUSH_SUPPORTED || !enabled) return;

    let cancelled = false;

    (async () => {
      const reg = await registerServiceWorker();
      if (!reg || cancelled) return;
      const existing = await reg.pushManager.getSubscription();
      if (!cancelled) setSubscribed(!!existing);
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const enable = useCallback(async (): Promise<boolean> => {
    if (!PUSH_SUPPORTED) {
      setError(t("usePushSubscription.thisBrowserDoesnT"));
      return false;
    }
    setBusy(true);
    setError(null);
    try {
      let perm = getNotificationPermission();
      if (perm !== "granted") {
        perm = await requestNotificationPermission();
        setPermission(perm);
      }
      if (perm !== "granted") {
        setError(t("usePushSubscription.notificationsWereBlockedCheck"));
        return false;
      }
      const sub = await subscribeToPush();
      setSubscribed(!!sub);
      return !!sub;
    } catch (err: any) {
      console.error("[push] enable failed", err);
      setError(err?.message || t("usePushSubscription.failedToEnableNotifications"));
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const disable = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await unsubscribeFromPush();
      setSubscribed(false);
    } catch (err: any) {
      console.error("[push] disable failed", err);
      setError(err?.message || t("usePushSubscription.failedToDisableNotifications"));
    } finally {
      setBusy(false);
    }
  }, []);

  return {
    supported: PUSH_SUPPORTED,
    permission,
    subscribed,
    busy,
    error,
    enable,
    disable,
  };
};
